import React, { memo, useMemo } from 'react'
import { ReactComponent as MapMarkerLanes } from 'assets/illustrations/MapMarkerLanes.svg'
import times from 'lodash/times'
import moment from 'moment'
import { Button } from 'transfix-ui/components/Button'
import { FormattedDateTime } from 'transfix-ui/components/FormattedDateTime'
import { IconButton } from 'transfix-ui/components/IconButton'
import { SortArrowToggle } from 'transfix-ui/components/SortArrowToggle'
import { Table, TableHead, TableRow, TableCell, TableBody } from 'transfix-ui/components/Table'
import { Typography } from 'transfix-ui/core'
import Box from 'transfix-ui/core/Box'
import Skeleton from 'transfix-ui/core/Skeleton'
import { IShipperRatesSortByEnum } from 'types/graphqlTypes'
import { EQUIPMENT_TYPE_KEY } from 'apps/marketplace/store/transfixLane/types'
import { getLargeEquipmentIcon } from 'apps/marketplace/utils/helpers/getIcon'
import { SortOrder } from 'apps/marketplace/utils/hooks/useFilters'
import { EllipsisIcon, SwitchIcon } from 'transfix-icons'
import { Avatar, OverflowMenu, useOverflowToggle, Tag, TAG_SIZE } from 'transfix-ui'
import { AllowedTimezones } from 'constants/AllowedTimezones'
import { convertToRegion } from 'constants/countriesStates'
import DISPLAY from 'constants/display'
import { EQUIPMENT } from 'constants/equipment'
import { ShipperRateObjectAugmented } from '../../../../utils/hooks/AwardedLanes/useAwardedLanesApi'
import { getJobStatusLabel, getJobStatusTagStyle } from '../../utils/getJobStatusTag'

interface IAwardedLanesTableRowProps {
  shipperRate: ShipperRateObjectAugmented
  onRowClick: (shipperRate: ShipperRateObjectAugmented) => void
  onOverflowClick: (shipperRate: ShipperRateObjectAugmented) => void
}

interface IAwardedLanesTableProps {
  loading: boolean
  sortField: IShipperRatesSortByEnum
  sortOrder: SortOrder
  onSortingChange: (field: IShipperRatesSortByEnum) => void
  children: React.ReactNode[]
}

interface ISortHeaderColumnProps {
  columnTitle: string
  columnSortField: IShipperRatesSortByEnum
  currentSortField: IShipperRatesSortByEnum
  currentSortOrder: SortOrder
  onSortClick: () => void
}

export const AwardedLanesTableRow = memo<IAwardedLanesTableRowProps>(
  ({ onOverflowClick, onRowClick, shipperRate }) => {
    const overflowOptions = [
      {
        itemComponent: (
          <div className='flex flex-row items-center'>
            <SwitchIcon />
            <span className='ml2'>Reassign Award</span>
          </div>
        ),
        label: 'delete',
        action: () => onOverflowClick(shipperRate),
      },
    ]
    const {
      carrierSalesRep,
      delivery,
      endDate,
      equipment,
      id,
      lane,
      loadType,
      monthDuration,
      pickup,
      salesRepName,
      shipperApplication,
      sourceStatus,
      sourcedCarrierRateCount,
      startDate,
      volumeConversions,
    } = shipperRate || {}
    const rowId = `awarded-lane-${id}`

    const { name } = shipperApplication || {}
    const { key } = EQUIPMENT.find(equip => equip.id === equipment?.parentId) || {}
    const equipmentKey = key as EQUIPMENT_TYPE_KEY

    const { id: carrierSalesRepId } = carrierSalesRep || {}
    const { weeklyVolume } = volumeConversions || {}

    const { clickOverflowTrigger, expanded, setExpanded } = useOverflowToggle()
    const startAt = moment(startDate).format()
    const endAt = endDate ? moment(endDate).format() : ''

    const jobLabel = getJobStatusLabel(sourceStatus)
    const jobStyle = getJobStatusTagStyle(sourceStatus)
    const showSourcedRateCount = Boolean(sourcedCarrierRateCount && sourcedCarrierRateCount > 0)

    return (
      <TableRow
        key={id || 0}
        highlightOnHover
        onClick={() => onRowClick(shipperRate)}
        dataTest={rowId}>
        <TableCell>
          <Box>{name || DISPLAY.empty}</Box>
          {showSourcedRateCount && (
            <Box className='text-accent'>
              {sourcedCarrierRateCount} Sourced Rate{sourcedCarrierRateCount === 1 ? '' : 's'}
            </Box>
          )}
        </TableCell>
        <TableCell>
          <Box>{lane}</Box>
          <Box className='text-secondary'>
            {convertToRegion(pickup?.region)} - {convertToRegion(delivery?.region)}
          </Box>
        </TableCell>
        <TableCell>{getLargeEquipmentIcon(equipmentKey, 'large', rowId)}</TableCell>
        <TableCell>{loadType}</TableCell>
        <TableCell>
          <Box>{weeklyVolume ? `${weeklyVolume}/week` : DISPLAY.empty}</Box>
          <Box className='text-secondary'>{monthDuration} month Award</Box>
        </TableCell>
        <TableCell>
          <FormattedDateTime
            abbreviated
            showTimezone={false}
            datetime={startAt}
            format='dateAndYear'
            displayTimezone={moment.tz.guess() as AllowedTimezones}
          />
        </TableCell>
        <TableCell>
          <FormattedDateTime
            abbreviated
            showTimezone={false}
            datetime={endAt}
            format='dateAndYear'
            displayTimezone={moment.tz.guess() as AllowedTimezones}
          />
        </TableCell>
        <TableCell>
          <Tag label={jobLabel} size={TAG_SIZE.small} tagStyle={jobStyle} />
        </TableCell>
        <TableCell>
          <Box display='inline' mr={2}>
            <Avatar key={`${carrierSalesRepId}-${salesRepName}`} name={salesRepName} />
          </Box>
          <Box display='inline'>{salesRepName}</Box>
        </TableCell>
        <TableCell>
          <OverflowMenu
            onExpanded={setExpanded}
            expanded={expanded}
            dropdownId={String(id)}
            trigger={
              <IconButton
                Icon={EllipsisIcon}
                color='tertiary'
                label='edit'
                data-test='edit-row'
                onClick={clickOverflowTrigger}
                size='large'
              />
            }
            options={overflowOptions}
            dataTest='sourced-rate-dropdown'
          />
        </TableCell>
      </TableRow>
    )
  }
)

export const AwardedLanesEmpty = () => {
  return (
    <TableRow>
      <TableCell colSpan={11}>
        <Box textAlign='center'>
          <Box mt={2} mb={2}>
            <MapMarkerLanes />
          </Box>
          <Box mb={1}>
            <Typography variant='h2'>No lanes found</Typography>
          </Box>
          <Box>Try adjusting search and filter settings</Box>
        </Box>
      </TableCell>
    </TableRow>
  )
}

export const SortHeaderColumn = memo<ISortHeaderColumnProps>(
  ({ columnSortField, columnTitle, currentSortField, currentSortOrder, onSortClick }) => {
    const sortDirection = useMemo(() => {
      return currentSortField === columnSortField ? currentSortOrder : undefined
    }, [columnSortField, currentSortField, currentSortOrder])

    return (
      <Button
        variant='text'
        onClick={onSortClick}
        endIcon={<SortArrowToggle direction={sortDirection} />}>
        {columnTitle}
      </Button>
    )
  }
)

export const AwardedLanesTable = memo<IAwardedLanesTableProps>(
  ({ children, loading, onSortingChange, sortField, sortOrder }) => {
    const skeletonLoaderRows = times(10, String)
    const skeletonLoaderColumns = times(10, String)

    const sortByVolumeHandler = () => onSortingChange(IShipperRatesSortByEnum.normalized_volume)
    const sortStartDateHandler = () => onSortingChange(IShipperRatesSortByEnum.start_date)
    const sortEndDateHandler = () => onSortingChange(IShipperRatesSortByEnum.end_date)

    return (
      <Table>
        <TableHead>
          <TableRow tableCellType='th'>
            <TableCell tableCellType='th'>Shipper</TableCell>
            <TableCell tableCellType='th'>Specific Lane</TableCell>
            <TableCell tableCellType='th'>Equipment</TableCell>
            <TableCell tableCellType='th'>Loading Method</TableCell>
            <TableCell tableCellType='th'>
              <SortHeaderColumn
                columnSortField={IShipperRatesSortByEnum.normalized_volume}
                columnTitle='Volume'
                currentSortField={sortField}
                currentSortOrder={sortOrder}
                onSortClick={sortByVolumeHandler}
              />
            </TableCell>
            <TableCell tableCellType='th'>
              <SortHeaderColumn
                columnSortField={IShipperRatesSortByEnum.start_date}
                columnTitle='Start Date'
                currentSortField={sortField}
                currentSortOrder={sortOrder}
                onSortClick={sortStartDateHandler}
              />
            </TableCell>
            <TableCell tableCellType='th'>
              <SortHeaderColumn
                columnSortField={IShipperRatesSortByEnum.end_date}
                columnTitle='End Date'
                currentSortField={sortField}
                currentSortOrder={sortOrder}
                onSortClick={sortEndDateHandler}
              />
            </TableCell>
            <TableCell tableCellType='th'>Job</TableCell>
            <TableCell tableCellType='th'>Assigned To</TableCell>
            <TableCell tableCellType='th'>Actions</TableCell>
          </TableRow>
        </TableHead>
        {loading ? (
          <TableBody>
            {skeletonLoaderRows.map((row, j) => (
              // eslint-disable-next-line react/no-array-index-key
              <TableRow key={`${row}-${j}`}>
                {skeletonLoaderColumns.map((col, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <TableCell key={`${col}-${i}`}>
                    <Skeleton width='100%' />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        ) : (
          <TableBody>{children}</TableBody>
        )}
      </Table>
    )
  }
)

export default AwardedLanesTable
